import { ref, shallowRef, computed, watch } from 'vue'
import { fetchOverview } from '../api/weatherApi.js'
import { useLocation } from './useLocation.js'
import { locale } from '../i18n/index.js'

const data = shallowRef(null)
const loading = ref(false)
const error = ref(null)
let inflight = null

/**
 * Single source of truth for the weather payload.
 * Mirrors how the OPERA `experience` bundle owns the vertical's data lifecycle:
 * it re-fetches whenever the location or the UI language changes — but NOT when
 * the temperature unit changes (that is a client-side, presentation-only concern).
 */
export function useWeather() {
  const { activeLocation, locationReady } = useLocation()

  async function load() {
    // Wait for the current city to be resolved from coordinates first: calling the
    // weather API with locHandle "0,0" returns HTTP 400 "invalid locHandle".
    if (!locationReady.value) { loading.value = true; return }
    const loc = activeLocation.value
    loading.value = true
    error.value = null
    try {
      const token = Symbol('req')
      inflight = token
      const json = await fetchOverview({ lat: loc.lat, lon: loc.lon, lang: locale.value })
      if (inflight !== token) return // stale response, discard
      data.value = json
    } catch (e) {
      error.value = e
    } finally {
      if (inflight === null || true) loading.value = false
    }
  }

  // Refetch when the place or the UI language changes — and once the current city
  // finishes resolving (locationReady flips true), which unblocks the first load.
  //
  // NOTE: the temperature unit is intentionally NOT a dependency. The API is
  // always asked for Celsius; °C/°F is resolved at render time by
  // useUnits().toDisplay(), which reads a module-level reactive ref. Toggling the
  // unit therefore re-renders every consumer without a single network call.
  watch(
    () => [activeLocation.value.id, locale.value, locationReady.value],
    () => load(),
    { immediate: false }
  )

  const weather = computed(() => data.value?.responses?.[0]?.weather?.[0] ?? null)
  const source = computed(() => data.value?.responses?.[0]?.source ?? null)
  const unitsMeta = computed(() => data.value?.units ?? null)

  return { data, weather, source, unitsMeta, loading, error, load }
}
